import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
} from 'recharts';
import {
  CATEGORY_STYLES,
  fmtCurrent,
  fmtChange,
  fmtMonth,
  fmtDay,
  changeColor,
} from './indicatorFormat';
import EconomicIndicatorDetailModal from './EconomicIndicatorDetailModal';

function EconomicIndicatorCard({ indicator, dateRange }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const category = indicator?.category || 'Industry';
  const styles = CATEGORY_STYLES[category] || CATEGORY_STYLES.Industry;

  const slice = useMemo(() => {
    const history = indicator?.history || [];
    if (!history.length) return [];
    if (!dateRange?.start || !dateRange?.end) return history;
    return history.filter((h) => h.date >= dateRange.start && h.date <= dateRange.end);
  }, [indicator, dateRange]);

  if (!indicator || indicator.error) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 h-full">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${styles.bg} ${styles.text}`}>{category}</span>
          <h4 className="text-sm font-semibold text-gray-300">{indicator?.display_title}</h4>
        </div>
        <p className="text-xs text-gray-500">{indicator?.error || 'Unavailable'}</p>
      </div>
    );
  }

  const { display_title, format, current, change_vs_prior, higher_is_bad,
          last_observation_date, next_release_date, nowcast } = indicator;

  return (
    <>
      <button
        type="button"
        onClick={() => setDetailOpen(true)}
        className="w-full text-left bg-gray-900 border border-gray-700 rounded-lg p-4 h-full flex flex-col cursor-pointer transition-colors hover:border-blue-500/40 hover:bg-gray-900/80 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
      >
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${styles.bg} ${styles.text}`}>{category}</span>
          <h4 className="text-sm font-semibold text-gray-300">{display_title}</h4>
        </div>
        <div className="flex items-baseline gap-3 mb-1 flex-wrap">
          <span className="text-2xl font-bold text-white">{fmtCurrent(current, format)}</span>
          <span className={`text-xs font-semibold ${changeColor(change_vs_prior, higher_is_bad)}`}>
            {fmtChange(change_vs_prior, format)} vs prior
          </span>
        </div>
        <div className="text-[10px] text-gray-500 mb-2">Last: {fmtMonth(last_observation_date)}</div>

        <div className="flex-1 min-h-[100px]">
          <ResponsiveContainer width="100%" height={100}>
            <AreaChart data={slice} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`econGrad-${indicator.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={styles.stroke} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={styles.stroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tick={{ fill: '#9ca3af', fontSize: 9 }}
                minTickGap={40}
              />
              <YAxis domain={['auto', 'auto']} hide />
              <Tooltip
                contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 11 }}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(v) => [fmtCurrent(Number(v), format), 'Value']}
              />
              <Area type="monotone" dataKey="value" stroke={styles.stroke} fill={`url(#econGrad-${indicator.key})`} strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-2 pt-2 border-t border-gray-800 text-[11px] text-gray-400 space-y-0.5">
          <div>Next release: <span className="text-gray-200">{fmtDay(next_release_date)}</span></div>
          {nowcast && (nowcast.forecast_value_yoy != null || nowcast.forecast_value_mom != null) && (
            <div>
              Nowcast:{' '}
              {nowcast.forecast_value_yoy != null && (
                <span className="text-gray-200">{nowcast.forecast_value_yoy.toFixed(2)}% YoY</span>
              )}
              {nowcast.forecast_value_mom != null && (
                <span className="text-gray-200 ml-2">{nowcast.forecast_value_mom.toFixed(2)}% MoM</span>
              )}
              <span className="text-gray-500"> (Cleveland Fed)</span>
            </div>
          )}
          <div className="text-[10px] text-blue-300/70">Click for release history →</div>
        </div>
      </button>

      <EconomicIndicatorDetailModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        indicator={indicator}
      />
    </>
  );
}

export default EconomicIndicatorCard;
